import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  Sparkles,
  Heart,
  AlertTriangle,
} from "lucide-react";
import { getVowsByCategory, type Vow } from "@/data/vows";
import {
  useCheckIn,
  TIME_SLOTS,
  type TimeSlotConfig,
  type JournalEntryInput,
} from "@/hooks/useCheckIn";

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

type Step = "slot-select" | "journal-entry" | "success";

export function CheckInFlow() {
  const navigate = useNavigate();
  const {
    nextPendingSlot,
    currentTimeSlot,
    completedSlots,
    saveJournalEntry,
  } = useCheckIn();

  const tugenden = getVowsByCategory("ten-virtues");

  // State
  const [step, setStep] = useState<Step>("slot-select");
  const [selectedSlot, setSelectedSlot] = useState<TimeSlotConfig>(
    nextPendingSlot ?? currentTimeSlot
  );

  // Journal entry fields
  const [positive, setPositive] = useState("");
  const [negative, setNegative] = useState("");
  const [selectedVirtueId, setSelectedVirtueId] = useState<number | null>(null);
  const [widmung, setWidmung] = useState("");
  const [versprechen, setVersprechen] = useState("");
  const [versprechenBisDatum, setVersprechenBisDatum] = useState("");

  const [saving, setSaving] = useState(false);

  // Handlers
  const handleSlotChange = useCallback(
    (value: string) => {
      const slot = TIME_SLOTS.find((ts) => ts.slot === Number(value));
      if (slot) setSelectedSlot(slot);
    },
    []
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const input: JournalEntryInput = {
        positive,
        negative,
        virtueId: selectedVirtueId,
        widmung,
        versprechen,
        versprechenBisDatum: versprechenBisDatum || null,
      };

      await saveJournalEntry(
        selectedSlot.slot as 1 | 2 | 3 | 4 | 5 | 6,
        input
      );

      setStep("success");
    } catch (err) {
      console.error("Failed to save journal entry:", err);
    } finally {
      setSaving(false);
    }
  }, [
    positive,
    negative,
    selectedVirtueId,
    widmung,
    versprechen,
    versprechenBisDatum,
    saveJournalEntry,
    selectedSlot.slot,
  ]);

  const hasContent = positive.trim() || negative.trim();

  // -------------------------------------------------------------------------
  // Step 1: Slot Selection
  // -------------------------------------------------------------------------
  if (step === "slot-select") {
    return (
      <div className="min-h-screen bg-background px-4 py-6">
        <div className="mx-auto max-w-lg space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Neuen Eintrag erstellen</h1>
              <p className="text-sm text-muted-foreground">
                Schritt 1 von 2: Zeitslot waehlen
              </p>
            </div>
          </div>

          {/* Slot picker */}
          <Card className="border-vajra-navy/50">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2 text-vajra-cyan">
                <Clock className="h-5 w-5" />
                <span className="font-medium">Welchen Zeitslot traegst du ein?</span>
              </div>

              <Select
                value={String(selectedSlot.slot)}
                onValueChange={handleSlotChange}
              >
                <SelectTrigger className="h-14 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((ts) => {
                    const isDone = completedSlots.has(ts.slot);
                    const isCurrent = ts.slot === currentTimeSlot.slot;
                    return (
                      <SelectItem
                        key={ts.slot}
                        value={String(ts.slot)}
                        disabled={isDone}
                      >
                        <span className="flex items-center gap-2">
                          {ts.label}
                          {isDone && (
                            <Badge
                              variant="outline"
                              className="text-xs text-emerald-400 border-emerald-500/40"
                            >
                              erledigt
                            </Badge>
                          )}
                          {isCurrent && !isDone && (
                            <Badge
                              variant="outline"
                              className="text-xs text-vajra-cyan border-vajra-cyan/40"
                            >
                              aktuell
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Button
            className="w-full h-14 text-lg bg-vajra-blue hover:bg-vajra-blue/90"
            onClick={() => setStep("journal-entry")}
          >
            Weiter
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Step 2: Journal Entry
  // -------------------------------------------------------------------------
  if (step === "journal-entry") {
    return (
      <div className="min-h-screen bg-background px-4 py-6">
        <div className="mx-auto max-w-lg space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setStep("slot-select")}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">Neuen Eintrag erstellen</h1>
              <p className="text-sm text-muted-foreground">
                {selectedSlot.label}
              </p>
            </div>
            <Badge
              variant="outline"
              className="text-vajra-cyan border-vajra-cyan/40"
            >
              Schritt 2/2
            </Badge>
          </div>

          {/* POSITIVES Section */}
          <Card className="border-emerald-500/30 bg-emerald-500/5">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/20">
                  <Check className="h-4 w-4 text-emerald-400" />
                </div>
                <Label className="text-base font-semibold text-emerald-400">
                  Positives
                </Label>
              </div>

              <Textarea
                placeholder="Was habe ich heute Gutes getan?"
                value={positive}
                onChange={(e) => setPositive(e.target.value)}
                className="min-h-[80px] bg-background/50 border-emerald-500/20 focus-visible:ring-emerald-500/40 resize-none"
              />

              {/* Tugend-Selector */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Zugehoerige Tugend (optional):
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {tugenden.map((vow: Vow) => {
                    const isSelected = selectedVirtueId === vow.id;
                    return (
                      <button
                        key={vow.id}
                        type="button"
                        onClick={() =>
                          setSelectedVirtueId(isSelected ? null : vow.id)
                        }
                        className={`
                          px-2.5 py-1 rounded-full text-xs font-medium
                          transition-all duration-150 border
                          ${
                            isSelected
                              ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400 ring-1 ring-emerald-500/30"
                              : "border-border/50 text-muted-foreground hover:border-emerald-500/30 hover:text-emerald-400/70"
                          }
                        `}
                      >
                        {vow.de.title.replace("Kein ", "").replace("Keine ", "")}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* NEGATIVES Section */}
          <Card className="border-rose-500/30 bg-rose-500/5">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-500/20">
                  <AlertTriangle className="h-4 w-4 text-rose-400" />
                </div>
                <Label className="text-base font-semibold text-rose-400">
                  Negatives
                </Label>
              </div>

              <Textarea
                placeholder="Was habe ich heute falsch gemacht?"
                value={negative}
                onChange={(e) => setNegative(e.target.value)}
                className="min-h-[80px] bg-background/50 border-rose-500/20 focus-visible:ring-rose-500/40 resize-none"
              />

              {/* Versprechen Box - appears when negative is filled */}
              {negative.trim().length > 0 && (
                <div className="space-y-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-rose-300">
                      Versprechen
                    </Label>
                    <Textarea
                      placeholder="z.B. 'Ich werde 7 Tage nicht schreien'"
                      value={versprechen}
                      onChange={(e) => setVersprechen(e.target.value)}
                      className="min-h-[60px] bg-background/50 border-rose-500/20 focus-visible:ring-rose-500/40 resize-none"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-rose-300">
                      Bis wann?
                    </Label>
                    <Input
                      type="date"
                      value={versprechenBisDatum}
                      onChange={(e) => setVersprechenBisDatum(e.target.value)}
                      min={new Date().toISOString().slice(0, 10)}
                      className="bg-background/50 border-rose-500/20 focus-visible:ring-rose-500/40"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* WIDMUNG Section */}
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500/20">
                  <Heart className="h-4 w-4 text-blue-400" />
                </div>
                <Label className="text-base font-semibold text-blue-400">
                  Widmung
                </Label>
              </div>

              <Textarea
                placeholder="Wem widme ich diese Praxis?"
                value={widmung}
                onChange={(e) => setWidmung(e.target.value)}
                className="min-h-[60px] bg-background/50 border-blue-500/20 focus-visible:ring-blue-500/40 resize-none"
              />
            </CardContent>
          </Card>

          {/* Save button */}
          <div className="sticky bottom-4 pt-2">
            <Button
              className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-900/30"
              onClick={handleSave}
              disabled={saving || !hasContent}
            >
              {saving ? (
                "Wird gespeichert..."
              ) : (
                <>
                  <Check className="mr-2 h-5 w-5" />
                  Eintrag speichern
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Step 3: Success
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="mx-auto max-w-lg text-center space-y-6">
        <div className="relative inline-flex">
          <div className="h-24 w-24 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
            <Sparkles className="h-12 w-12 text-emerald-400" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-emerald-400">
            Eintrag gespeichert!
          </h1>
          <p className="text-muted-foreground">
            {selectedSlot.label} — gut gemacht.
          </p>
        </div>

        <Button
          className="w-full h-14 text-lg bg-vajra-blue hover:bg-vajra-blue/90"
          onClick={() => navigate("/")}
        >
          Zurück zur Übersicht
        </Button>
      </div>
    </div>
  );
}
